import { createElement } from 'react';
import { toast } from 'sonner';

import { type CurrencyCode, formatCurrencyValue, isCurrencyCode } from '@/shared/lib/currency';
import { GameEventToast } from '@/shared/ui/toasts/GameEventToast';
import { SystemToast } from '@/shared/ui/toasts/SystemToast';

function gameEvent(
  game: string,
  type: 'win' | 'loss' | 'info',
  mainLine: string,
  subLine?: string,
  countUpAmount?: number,
  countUpCurrency: CurrencyCode = 'TON',
) {
  toast.custom(
    () =>
      createElement(GameEventToast, {
        game,
        type,
        mainLine,
        subLine,
        countUpAmount,
        countUpCurrency,
      }),
    { duration: 4000 },
  );
}

function safeCurrency(currency?: string): CurrencyCode {
  return currency && isCurrencyCode(currency) ? currency : 'TON';
}

const toastService = {
  success(message: string, description?: string) {
    toast.custom(() =>
      createElement(SystemToast, { title: message, description, type: 'success' }),
    );
  },

  error(message: string, description?: string) {
    toast.custom(() => createElement(SystemToast, { title: message, description, type: 'error' }));
  },

  info(message: string, description?: string) {
    toast.custom(() => createElement(SystemToast, { title: message, description, type: 'info' }));
  },

  // ── Game events ──────────────────────────────────────────────────────

  rollsWin(payout: number, currency?: string) {
    gameEvent('Pure Rolls', 'win', '', 'You won the round!', payout, safeCurrency(currency));
  },

  rollsRoundEnd(winnerUsername: string, payout: number, currency?: string) {
    const safe = safeCurrency(currency);
    gameEvent(
      'Pure Rolls',
      'info',
      `@${winnerUsername} won`,
      `${formatCurrencyValue(payout, safe)} payout`,
    );
  },

  jettonWin(payout: number, multiplier: number, currency?: string) {
    gameEvent(
      'JetTON',
      'win',
      '',
      `Cashed out at ${multiplier.toFixed(2)}×`,
      payout,
      safeCurrency(currency),
    );
  },

  jettonCrash(crashMultiplier: number) {
    gameEvent('JetTON', 'loss', `Crashed at ${crashMultiplier.toFixed(2)}×`, 'Bet lost');
  },

  minesRadarHint() {
    gameEvent('Mines', 'info', 'Lucky hint!', 'A safe cell has been revealed for you');
  },

  balloonLucky() {
    gameEvent('Balloon', 'info', "You're lucky today!", 'Your bet is protected this round');
  },

  balloonLuckySaved(betAmount: number, currency?: string) {
    const safe = safeCurrency(currency);
    gameEvent(
      'Balloon',
      'info',
      'Lucky save!',
      `Your bet ${formatCurrencyValue(betAmount, safe)} refunded`,
    );
  },
};

export { toastService };
